/**
 * FCM service — permission, Service Worker registration, token generation /
 * refresh, foreground listener, and storing the token in Supabase.
 *
 * ISOLATED from the rest of the app. Import from here only where push is needed.
 * Every function is defensive: if Firebase isn't configured or the browser lacks
 * support, they resolve to a safe value instead of throwing.
 */

import { getMessaging, getToken, onMessage, deleteToken, isSupported } from 'firebase/messaging';
import { getFirebaseApp, VAPID_KEY, isFirebaseConfigured } from './firebaseConfig';
import { supabase } from '../config/supabase';

let _messaging = null;
let _swReg = null;

/** Whether push is usable in this browser/context. */
export const pushSupported = async () => {
  try {
    return isFirebaseConfigured()
      && 'serviceWorker' in navigator
      && 'Notification' in window
      && (await isSupported());
  } catch (_) { return false; }
};

/** Current Notification permission: 'granted' | 'denied' | 'default' | 'unsupported'. */
export const getPermissionStatus = () =>
  (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

/** Register the FCM background Service Worker (idempotent). */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) throw new Error('Service Worker non supporté');
  if (_swReg) return _swReg;
  _swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
  return _swReg;
};

const getMessagingInstance = async () => {
  if (_messaging) return _messaging;
  const app = getFirebaseApp();
  if (!app) throw new Error('Firebase non configuré (variables VITE_FIREBASE_* manquantes)');
  _messaging = getMessaging(app);
  return _messaging;
};

/**
 * Request permission + get the FCM device token. Returns the token string, or
 * throws with a clear message. Also stores the token in Supabase.
 */
export const requestPermissionAndToken = async () => {
  if (!(await pushSupported())) throw new Error('Notifications push non supportées sur cet appareil/navigateur');
  if (!VAPID_KEY) throw new Error('VITE_FIREBASE_VAPID_KEY manquant (clé Web Push)');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Permission refusée pour les notifications');

  const reg = await registerServiceWorker();
  const messaging = await getMessagingInstance();
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
  if (!token) throw new Error('Impossible d\'obtenir le token FCM');

  await saveTokenToSupabase(token);
  return token;
};

/** Get the current token WITHOUT prompting (only if already granted). */
export const getCurrentToken = async () => {
  if (!(await pushSupported()) || getPermissionStatus() !== 'granted' || !VAPID_KEY) return null;
  try {
    const reg = await registerServiceWorker();
    const messaging = await getMessagingInstance();
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    if (token) saveTokenToSupabase(token).catch(() => {});
    return token || null;
  } catch (_) { return null; }
};

/** Delete the token (unsubscribe) and remove it from Supabase. */
export const revokeToken = async () => {
  try {
    const token = await getCurrentToken();
    const messaging = await getMessagingInstance();
    await deleteToken(messaging);
    if (token) await removeTokenFromSupabase(token);
    return true;
  } catch (_) { return false; }
};

/**
 * Listen for FOREGROUND messages. `cb(payload)` is called for each. Returns an
 * unsubscribe function.
 */
export const onForegroundMessage = (cb) => {
  let unsub = () => {};
  getMessagingInstance()
    .then(messaging => { unsub = onMessage(messaging, cb); })
    .catch(() => {});
  return () => unsub();
};

// ── Supabase persistence (NEW dedicated table, does not touch existing ones) ──
export const saveTokenToSupabase = async (token) => {
  if (!token) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const agencyId = (typeof window !== 'undefined' && window.__AGENCY_ID__) || user?.id || null;
    // Upsert on the token so a device has exactly one row (token refresh safe).
    await supabase.from('fcm_tokens').upsert({
      token,
      uid: user?.id || null,
      agency_id: agencyId,
      user_agent: navigator.userAgent,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'token' });
  } catch (err) {
    console.warn('[FCM] saveTokenToSupabase failed:', err?.message);
  }
};

export const removeTokenFromSupabase = async (token) => {
  try { await supabase.from('fcm_tokens').delete().eq('token', token); } catch (_) {}
};
